import React, { useEffect, useState } from "react";
import { Autocomplete, Card, Grid, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import { postAPICall, getAPICall } from "lib/callApi";
import { FETCH_CACHE, FLUSH_CACHE, FETCH_COMPANY_LIST } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import MDInput from "components/MDInput";
import Swal from "sweetalert2";

function CacheManager() {
  const [feedback, setFeedback] = useState({
    text: "",
    color: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState();
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [cacheValue, setCacheValue] = useState(null);

  const flushCache = async () => {
    setIsLoading(true);
    setFeedback({
      text: "",
      color: "",
    });
    const response = await postAPICall(FLUSH_CACHE, true, { companyId: selectedCompany.id });
    if (response?.data?.success) {
      setFeedback({ text: "Success!", color: "green" });
    } else {
      setFeedback({ text: "Failure!", color: "red" });
    }
    setIsLoading(false);
  };

  const fetchCacheList = async () => {
    setSearchLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.append("", search);

    const response = await getAPICall(
      `${FETCH_CACHE}/${searchParams.toString().replace("=", "")}`,
      true
    );

    if (response?.data?.success) {
      setCacheValue({ key: search, value: response.data.data || "" });
    } else {
      setCacheValue(null);
    }
    setSearchLoading(false);
  };

  const saveCacheValue = async () => {
    setIsLoading(true);
    const response = await postAPICall(FETCH_CACHE, true, cacheValue);

    if (response?.data?.success) {
      Swal.fire({
        icon: "success",
        confirmButtonColor: "#267FEA",
        title: "Updated",
        text: "Cache has been updated successfully!",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: response.data.message,
      });
    }
    setIsLoading(false);
  };

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(FETCH_COMPANY_LIST, true);

      if (result?.data?.success) {
        setCompanyList([
          {
            id: "all",
            name: "All",
          },
          ...(result.data.data.map((company) => ({ id: company.id, name: company?.name })) || []),
        ]);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox sx={{ pt: 3 }}>
        <Card
          sx={{
            minHeight: "400px",
          }}
        >
          <MDBox p={3} pb={0} lineHeight={1}>
            <MDTypography variant="h5" fontWeight="medium">
              Cache Manager
            </MDTypography>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              mb={2}
              spacing={2}
            >
              <Grid item xs={12} md={4} lg={3} xl={2}>
                <Autocomplete
                  fullWidth
                  id="company-autocomplete"
                  options={companyList}
                  value={selectedCompany || null}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) => {
                    setSelectedCompany(value);
                    setFeedback({
                      text: "",
                      color: "",
                    });
                  }}
                  renderInput={(params) => <TextField {...params} label="Company" />}
                />
              </Grid>
            </Grid>
            <MDBox display="flex" mt={3} pr={3}>
              <MDButton
                color="primaryRed"
                type="submit"
                onClick={flushCache}
                disabled={isLoading || !selectedCompany}
              >
                Flush Cache
              </MDButton>
              <MDTypography
                variant="h6"
                pl={2}
                pt={1}
                sx={{
                  fontWeight: 400,
                  color: feedback.color,
                }}
              >
                {feedback.text}
              </MDTypography>
            </MDBox>

            <MDBox my={4}>
              <MDInput
                label="Search"
                disabled={isLoading || searchLoading}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
              />
              <MDButton
                color="primaryRed"
                type="submit"
                onClick={fetchCacheList}
                disabled={isLoading || searchLoading || !search}
                sx={{ my: 2 }}
              >
                Search Cache Key
              </MDButton>
            </MDBox>

            <MDBox my={4}>
              <MDInput
                label={`Cache Value  ${cacheValue?.key ? `of ${cacheValue.key}` : ""}`}
                disabled={isLoading}
                value={cacheValue?.value || ""}
                onChange={(e) => setCacheValue({ ...cacheValue, value: e.target.value })}
                fullWidth
              />
              <MDButton
                color="primaryRed"
                type="submit"
                onClick={saveCacheValue}
                disabled={isLoading || !cacheValue}
                sx={{ my: 2 }}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default CacheManager;
